import { useState, useEffect } from "react";
import { Localized, useLocalization } from "@fluent/react";
import { Button, Checkbox, Row } from "@narmi/design_system";
import { Dialog } from "cerulean";
import styles from "./BulkTemplates.module.scss";

type ArchiveTemplateDialogProps = {
  isOpen: boolean;
  templateName: string;
  pendingPaymentsCount: number;
  onClose: () => void;
  onArchive: () => void;
};

const ArchiveTemplateDialog = ({
  isOpen,
  templateName,
  pendingPaymentsCount,
  onClose,
  onArchive,
}: ArchiveTemplateDialogProps) => {
  const { l10n } = useLocalization();
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAcknowledged(false);
    }
  }, [isOpen]);

  return (
    <Dialog
      width="min(595px, 90vw)"
      isOpen={isOpen}
      onUserDismiss={onClose}
      title={l10n.getString("archive-template-dialog-title", {
        templateName,
      })}
      footer={
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              onClick={onClose}
              testId="archive-template-cancel"
            >
              {l10n.getString("button-cancel")}
            </Button>
          </Row.Item>
          <Row.Item shrink>
            <Button
              onClick={onArchive}
              testId="archive-template-confirm"
              disabled={!acknowledged}
            >
              {l10n.getString("label-archive")}
            </Button>
          </Row.Item>
        </Row>
      }
    >
      <div className="margin--bottom--l">
        <p>
          {l10n.getString("archive-template-dialog-content", {
            templateName,
          })}
        </p>
        {pendingPaymentsCount > 0 && (
          <Localized
            id="archive-template-dialog-pending-activity"
            vars={{ pendingPaymentsCount }}
            elems={{ bold: <strong />, boldCount: <strong /> }}
          >
            <p className="margin--top--s">
              <strong>Pending activity:</strong> There are{" "}
              <strong>{pendingPaymentsCount} bulk payments</strong> currently
              scheduled using this template.
            </p>
          </Localized>
        )}
      </div>
      <Checkbox
        label={l10n.getString("archive-template-dialog-cta")}
        name="cancel-scheduled-payments"
        checked={acknowledged}
        onChange={() => setAcknowledged(!acknowledged)}
      />
      <p
        className={`fontSize--s fontColor--secondary margin--top--xxs ${styles.archiveDialogCtaSubCopy}`}
      >
        {l10n.getString("archive-template-dialog-cta-sub-copy")}
      </p>
    </Dialog>
  );
};

export default ArchiveTemplateDialog;
